import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { Navigate, NoContextOutlet } from "src/router";
import { PermissionedRoute } from "src/components/router/permissioned-route";
import { useLifecycleStudioEnabled } from "src/hooks/use-lifecycle-studio-enabled";
import { LcmIntroDialog } from "src/pages/lifecycle-studio/components/lcm-intro-dialog";
import { lazyNamed } from "src/utils/lazy-named";

// Campaign projects
const projectCampaignsImport = () => import("./projects");
const ProjectCampaignsPage = lazyNamed(
  projectCampaignsImport,
  "ProjectCampaignsPage",
);

export const preloads: RoutePreload[] = [
  ["/lifecycle-studio", projectCampaignsImport],
];
const ProjectCampaignDetail = lazyNamed(
  () => import("./projects/detail"),
  "ProjectCampaignDetail",
);
const ProjectCampaignDefaultTabRedirect = lazyNamed(
  () => import("./projects/default-tab-redirect"),
  "ProjectCampaignDefaultTabRedirect",
);
const ProjectCampaignPlanning = lazyNamed(
  () => import("./projects/planning"),
  "ProjectCampaignPlanning",
);
const CandidatesPanel = lazyNamed(
  () => import("./projects/message-plans/candidates-panel"),
  "CandidatesPanel",
);
const PlanDetailPage = lazyNamed(
  () => import("./projects/message-plans/plan-detail-page"),
  "PlanDetailPage",
);
const ProjectCampaignOrchestration = lazyNamed(
  () => import("./projects/orchestration"),
  "ProjectCampaignOrchestration",
);
const CreateProjectCampaignPage = lazyNamed(
  () => import("./projects/create"),
  "CreateProjectCampaignPage",
);
const CandidateMessageDetailLayout = lazyNamed(
  () => import("./projects/message-plans/candidate-message-detail"),
  "CandidateMessageDetailLayout",
);
const CandidateMessageEditor = lazyNamed(
  () => import("./projects/message-plans/candidate-message-detail"),
  "CandidateMessageEditor",
);
const PrepareForDeliveryPage = lazyNamed(
  () =>
    import("./emails/email-editor/dynamic-content/prepare-for-delivery-page"),
  "PrepareForDeliveryPage",
);
const PrepareForDeliveryWorkspace = lazyNamed(
  () =>
    import("./emails/email-editor/dynamic-content/prepare-for-delivery-page"),
  "PrepareForDeliveryWorkspace",
);
const CandidateCopyEditor = lazyNamed(
  () => import("./projects/message-plans/candidate-message-detail"),
  "CandidateCopyEditor",
);
const CopyPrepareForDeliveryPage = lazyNamed(
  () => import("./copy/prepare-for-delivery-page"),
  "PrepareForDeliveryPage",
);
const CopyPrepareForDeliveryWorkspace = lazyNamed(
  () => import("./copy/prepare-for-delivery-page"),
  "PrepareForDeliveryWorkspace",
);

// Messages
const OrganizedMessagesPage = lazyNamed(
  () => import("./messages/organized-messages-page"),
  "OrganizedMessagesPage",
);
const LifecycleStudioEmail = lazyNamed(
  () => import("./messages/email"),
  "LifecycleStudioEmail",
);
const GenerateEmail = lazyNamed(
  () => import("./generate/generate-email"),
  "GenerateEmail",
);
const ContentLibraryMessageWorkspace = lazyNamed(
  () => import("./messages/content-library-message-workspace"),
  "ContentLibraryMessageWorkspace",
);
const ContentCopy = lazyNamed(() => import("./copy/copy"), "ContentCopy");
const ContentCopyEditor = lazyNamed(
  () => import("./copy/content-copy-editor"),
  "ContentCopyEditor",
);
const GenerateCopy = lazyNamed(() => import("./copy/generate"), "GenerateCopy");

// Templates
const LifecycleStudioTemplates = lazyNamed(
  () => import("./templates"),
  "LifecycleStudioTemplates",
);
const ImportEmailDrawer = lazyNamed(
  () => import("./emails/components/import-email-drawer"),
  "ImportEmailDrawer",
);
const TemplateEditWorkspace = lazyNamed(
  () => import("./templates/template-edit-workspace"),
  "TemplateEditWorkspace",
);

// Images
const LifecycleStudioImages = lazyNamed(
  () => import("./images"),
  "LifecycleStudioImages",
);
const ContentImages = lazyNamed(
  () => import("./images/content-images"),
  "ContentImages",
);
const ContentImage = lazyNamed(() => import("./images/image"), "ContentImage");
const TagsSettings = lazyNamed(() => import("./settings/tags"), "TagsSettings");
const ContentNodeEditor = lazyNamed(
  () => import("./image-editor"),
  "ContentNodeEditor",
);

const FeatureFlagGuard = ({ children }: { children: React.ReactNode }) => {
  const lifecycleStudioEnabled = useLifecycleStudioEnabled();
  if (!lifecycleStudioEnabled) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

export const routes: RouteObject[] = [
  {
    path: "lifecycle-studio",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <PermissionedRoute
        permission={{
          v2: { resource: "workspace", grant: "can_view_agents" },
        }}
        redirect="/"
      >
        <FeatureFlagGuard>
          <LcmIntroDialog />
          <NoContextOutlet />
        </FeatureFlagGuard>
      </PermissionedRoute>
    ),
    children: [
      {
        path: "projects",
        spacesAccess: "hidden-from-limited-view",
        element: <ProjectCampaignsPage />,
      },
      {
        // Full-page create wizard — a standalone route (not a child of the
        // projects list) so it takes over the entire content area instead of
        // rendering on top of the campaigns table.
        path: "projects/new",
        spacesAccess: "hidden-from-limited-view",
        element: <CreateProjectCampaignPage />,
      },
      {
        path: "projects/:campaignId",
        spacesAccess: "hidden-from-limited-view",
        element: <ProjectCampaignDetail />,
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            // Lands on Create when the campaign has generated candidates,
            // otherwise on Plan. See ProjectCampaignDefaultTabRedirect.
            element: <ProjectCampaignDefaultTabRedirect />,
          },
          {
            // Single route for both the read view (`/planning`) and the
            // full-screen brief editor (`/planning/edit`). One route means one
            // mounted `ProjectCampaignPlanning` element across the toggle, so
            // the chat sidebar isn't torn down and reloaded. The page reads
            // edit mode from the URL; detail.tsx hides the project chrome.
            path: "planning/*",
            spacesAccess: "hidden-from-limited-view",
            element: <ProjectCampaignPlanning />,
          },
          // Legacy paths from before the Ideation and Plans tabs were merged
          // into Planning.
          {
            path: "ideation",
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="../planning" replace />,
          },
          {
            path: "plans",
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="../planning" replace />,
          },
          {
            path: "candidates",
            spacesAccess: "hidden-from-limited-view",
            element: <CandidatesPanel />,
          },
          {
            // Full-page editor for a single message plan. Rendered chromeless
            // (project tabs hidden) by detail.tsx, like the message editor.
            path: "candidates/plans/:planId",
            spacesAccess: "hidden-from-limited-view",
            element: <PlanDetailPage />,
          },
          {
            path: "candidates/messages/:emailId",
            spacesAccess: "hidden-from-limited-view",
            element: <CandidateMessageDetailLayout />,
            children: [
              {
                index: true,
                spacesAccess: "hidden-from-limited-view",
                element: <Navigate to="edit" replace />,
              },
              {
                path: "edit",
                spacesAccess: "hidden-from-limited-view",
                element: <CandidateMessageEditor />,
              },
              {
                // The variants list renders inside the builder at
                // `/edit?variantsList=true`; CandidateMessageDetailLayout
                // redirects this path there. This element is never rendered.
                path: "variants",
                spacesAccess: "hidden-from-limited-view",
                element: <CandidateMessageEditor />,
              },
              {
                path: "prepare-for-delivery",
                spacesAccess: "hidden-from-limited-view",
                element: <PrepareForDeliveryPage />,
              },
            ],
          },
          {
            path: "candidates/messages/copy/:copyId/edit",
            spacesAccess: "hidden-from-limited-view",
            element: <CandidateCopyEditor />,
          },
          {
            path: "candidates/messages/copy/:copyId/prepare-for-delivery",
            spacesAccess: "hidden-from-limited-view",
            element: <CopyPrepareForDeliveryPage />,
          },
          {
            path: "orchestration",
            spacesAccess: "hidden-from-limited-view",
            element: <ProjectCampaignOrchestration />,
          },
        ],
      },
      // The legacy Campaigns and Concepts surfaces were removed. Their IDs have
      // no v2 equivalent, so bookmarked deep links land on the Campaigns list
      // rather than a 404.
      {
        path: "campaigns/*",
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="/lifecycle-studio/projects" replace />,
      },
      {
        path: "concepts/*",
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="/lifecycle-studio/projects" replace />,
      },
      {
        path: "generate/*",
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="/lifecycle-studio/projects" replace />,
      },
      {
        path: "messages",
        spacesAccess: "hidden-from-limited-view",
        element: <OrganizedMessagesPage />,
        children: [
          {
            path: ":emailId",
            spacesAccess: "hidden-from-limited-view",
            element: <LifecycleStudioEmail />,
          },
          {
            path: "copy/:copyId",
            spacesAccess: "hidden-from-limited-view",
            element: <ContentCopy />,
          },
        ],
      },
      {
        path: "messages/build",
        spacesAccess: "hidden-from-limited-view",
        element: <GenerateEmail />,
      },
      {
        path: "messages/:emailId/edit",
        spacesAccess: "hidden-from-limited-view",
        element: <ContentLibraryMessageWorkspace />,
      },
      {
        path: "messages/:emailId/prepare-for-delivery",
        spacesAccess: "hidden-from-limited-view",
        element: <PrepareForDeliveryWorkspace />,
      },
      {
        path: "messages/:emailId/variants",
        spacesAccess: "hidden-from-limited-view",
        element: <ContentLibraryMessageWorkspace />,
      },
      {
        path: "messages/copy/:copyId/edit",
        spacesAccess: "hidden-from-limited-view",
        element: <ContentCopyEditor />,
      },
      {
        path: "messages/copy/:copyId/prepare-for-delivery",
        spacesAccess: "hidden-from-limited-view",
        element: <CopyPrepareForDeliveryWorkspace />,
      },
      {
        path: "messages/copy/build",
        spacesAccess: "hidden-from-limited-view",
        element: <GenerateCopy />,
      },
      {
        path: "templates",
        spacesAccess: "hidden-from-limited-view",
        element: <LifecycleStudioTemplates />,
        children: [
          {
            path: "import",
            spacesAccess: "hidden-from-limited-view",
            element: <ImportEmailDrawer />,
          },
          {
            path: ":emailId",
            spacesAccess: "hidden-from-limited-view",
            element: <LifecycleStudioEmail />,
          },
        ],
      },
      {
        path: "templates/:emailId/edit",
        spacesAccess: "hidden-from-limited-view",
        element: <TemplateEditWorkspace />,
      },
      {
        path: "images",
        spacesAccess: "hidden-from-limited-view",
        element: <LifecycleStudioImages />,
        children: [
          {
            // Gallery layout route — ContentImages renders for both the index
            // and :imageId paths. ContentImage renders in ContentImages' outlet.
            element: <ContentImages />,
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                index: true,
                spacesAccess: "hidden-from-limited-view",
                element: null,
              },
              {
                path: ":imageId",
                spacesAccess: "hidden-from-limited-view",
                element: <ContentImage />,
              },
            ],
          },
          {
            path: "tags",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <PermissionedRoute
                permission={{
                  v2: { resource: "workspace", grant: "can_configure_agents" },
                }}
                redirect="/lifecycle-studio/images"
              >
                <TagsSettings />
              </PermissionedRoute>
            ),
          },
        ],
      },
      {
        path: "images/edit/:sessionId",
        spacesAccess: "hidden-from-limited-view",
        element: <ContentNodeEditor />,
      },
      {
        path: "image-editor/:sessionId",
        spacesAccess: "hidden-from-limited-view",
        element: <ContentNodeEditor />,
      },
    ],
  },
];
